import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaMapMarkedAlt, FaSave, FaTimes } from 'react-icons/fa';
import styles from './styles/ShippingZones.module.css';
import shared from './styles/adminShared.module.css';
import PrimaryButton from '../PrimaryButton';

const SCHEDULE_KEY = 'shipping_schedule_v1';

interface ZoneSchedule {
    nombre: string;
    manana: string;
    tarde: string;
}

const DEFAULT_SCHEDULE: ZoneSchedule[] = [
    { nombre: 'Macrocentro (dentro de Avellaneda, 27 de Febrero y el río)', manana: 'Lunes, Miércoles y Viernes', tarde: 'Martes, Jueves y Sábado' },
    { nombre: 'Fisherton, Funes y Roldán', manana: 'Martes, Jueves y Sábado', tarde: 'Lunes, Miércoles y Viernes' },
    { nombre: 'Zona Norte (dentro de Génova, Circunvalación y Granadero Baigorria)', manana: 'Lunes', tarde: 'Jueves' },
    { nombre: 'Zona Oeste (después de Avellaneda, hasta Circunvalación y antes de Fisherton)', manana: 'Viernes', tarde: 'Martes' },
    { nombre: 'Zona Sur (dentro de Francia, 27 de Febrero y Circunvalación)', manana: 'Miércoles', tarde: 'Sábado' },
];

export interface ShippingZone {
    id: number;
    nombre: string;
    costo: number;
    minimo_envio_gratis: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ShippingZones: React.FC = () => {
    const [zonas, setZonas] = useState<ShippingZone[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');

    // Schedule state
    const [schedule, setSchedule] = useState<ZoneSchedule[]>(() => {
        try {
            const stored = localStorage.getItem(SCHEDULE_KEY);
            return stored ? JSON.parse(stored) : DEFAULT_SCHEDULE;
        } catch {
            return DEFAULT_SCHEDULE;
        }
    });
    const [editingScheduleIdx, setEditingScheduleIdx] = useState<number | null>(null);
    const [scheduleEditData, setScheduleEditData] = useState<ZoneSchedule>({ nombre: '', manana: '', tarde: '' });

    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        nombre: '',
        costo: 0,
        minimo_envio_gratis: 0
    });

    useEffect(() => {
        fetchZonas();
    }, []);

    const fetchZonas = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/admin/zonas-envio`);
            const data = await res.json();
            if (Array.isArray(data)) {
                setZonas(data);
            }
        } catch (err) {
            setError('Error al cargar zonas de envío');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'number' ? Number(value) : value
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMsg('');

        if (!formData.nombre.trim()) {
            setError('El nombre de la zona es obligatorio');
            return;
        }

        const url = isEditing
            ? `${API_URL}/admin/zonas-envio/${currentId}`
            : `${API_URL}/admin/zonas-envio`;

        const method = isEditing ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setMsg(isEditing ? 'Zona actualizada correctamente' : 'Zona creada correctamente');
                fetchZonas();
                resetForm();
            } else {
                setError('Error al guardar la zona');
            }
        } catch (err) {
            setError('Error de conexión');
        }

        setTimeout(() => setMsg(''), 3000);
    };

    const handleEdit = (zona: ShippingZone) => {
        setIsEditing(true);
        setCurrentId(zona.id);
        setFormData({
            nombre: zona.nombre,
            costo: zona.costo,
            minimo_envio_gratis: zona.minimo_envio_gratis
        });
        window.scrollTo(0, 0);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('¿Seguro que deseas eliminar esta zona de envío?')) return;

        try {
            const res = await fetch(`${API_URL}/admin/zonas-envio/${id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setMsg('Zona eliminada');
                fetchZonas();
            } else {
                setError('Error al eliminar');
            }
        } catch (err) {
            setError('Error de conexión');
        }

        setTimeout(() => setMsg(''), 3000);
    };

    const resetForm = () => {
        setIsEditing(false);
        setCurrentId(null);
        setFormData({
            nombre: '',
            costo: 0,
            minimo_envio_gratis: 0
        });
        setError('');
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3><FaMapMarkedAlt style={{ marginRight: '10px' }} /> Zonas de Envío</h3>
            </div>

            <div className={styles.content}>
                {/* Form & List Section */}
                <div className={styles.managementSection}>
                    <div className={styles.formCard}>
                        <h4>{isEditing ? 'Editar Zona' : 'Nueva Zona'}</h4>

                        {error && <div className={styles.error}>{error}</div>}
                        {msg && <div className={styles.success}>{msg}</div>}

                        <form onSubmit={handleSubmit} className={styles.form}>
                            <div className={styles.formGroup}>
                                <label>Nombre de la Zona</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    placeholder="Ej: Rosario, Zona Norte..."
                                    required
                                />
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label>Costo del Envío ($)</label>
                                    <input
                                        type="number"
                                        name="costo"
                                        value={formData.costo}
                                        onChange={handleChange}
                                        min="0"
                                        step="1"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Mínimo Envío Gratis ($)</label>
                                    <input
                                        type="number"
                                        name="minimo_envio_gratis"
                                        value={formData.minimo_envio_gratis}
                                        onChange={handleChange}
                                        min="0"
                                        step="1"
                                    />
                                    <small className={styles.helpText}>Dejar en 0 si no aplica.</small>
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <PrimaryButton type="submit">
                                    {isEditing ? 'Guardar Cambios' : 'Crear Zona'}
                                </PrimaryButton>
                                {isEditing && (
                                    <button type="button" onClick={resetForm} className={styles.btnCancel}>
                                        Cancelar
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className={`${styles.listCard} ${shared.asyncRegion}`}>
                        <h4>Zonas Configuradas</h4>
                        {loading ? <p className={shared.loadingState}>Cargando...</p> : (
                            <table className={styles.zonesTable}>
                                <thead>
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Costo</th>
                                        <th>Mín. p/ Gratis</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {zonas.length > 0 ? (
                                        zonas.map(z => (
                                            <tr key={z.id}>
                                                <td>{z.nombre}</td>
                                                <td>${z.costo.toLocaleString()}</td>
                                                <td>
                                                    {z.minimo_envio_gratis > 0
                                                        ? `$${z.minimo_envio_gratis.toLocaleString()}`
                                                        : '-'}
                                                </td>
                                                <td>
                                                    <div className={styles.actionsCell}>
                                                        <button
                                                            onClick={() => handleEdit(z)}
                                                            className={styles.btnEdit}
                                                            title="Editar zona"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(z.id)}
                                                            className={styles.btnDelete}
                                                            title="Eliminar zona"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                                                No hay zonas de envío configuradas.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Map Section */}
                <div className={styles.mapSection}>
                    <h4>Mapa de Referencia</h4>
                    <div className={styles.mapContainer}>
                        <iframe
                            src="https://www.google.com/maps/d/embed?mid=1Mbcc8_pPn2kttHMp0xKftzxGQrcTld8&ehbc=2E312F"
                            width="100%"
                            height="500"
                            title="Mapa Zonas de Envío"
                            style={{ border: 0, borderRadius: '8px' }}
                            allowFullScreen
                        ></iframe>
                    </div>
                </div>

                {/* Schedule Section */}
                <div className={styles.mapSection}>
                    <h4>Días y Horarios Disponibles por Zona</h4>
                    <table className={styles.zonesTable}>
                        <thead>
                            <tr>
                                <th style={{ width: '32px' }}>#</th>
                                <th>Zona</th>
                                <th>Mañana</th>
                                <th>Tarde</th>
                                <th style={{ width: '60px' }}>Editar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schedule.map((z, idx) => (
                                <tr key={idx}>
                                    {editingScheduleIdx === idx ? (
                                        <>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={scheduleEditData.nombre}
                                                    onChange={e => setScheduleEditData({ ...scheduleEditData, nombre: e.target.value })}
                                                    style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={scheduleEditData.manana}
                                                    onChange={e => setScheduleEditData({ ...scheduleEditData, manana: e.target.value })}
                                                    style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={scheduleEditData.tarde}
                                                    onChange={e => setScheduleEditData({ ...scheduleEditData, tarde: e.target.value })}
                                                    style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
                                                />
                                            </td>
                                            <td>
                                                <div className={styles.actionsCell}>
                                                    <button
                                                        className={styles.btnEdit}
                                                        title="Guardar"
                                                        onClick={() => {
                                                            const updated = schedule.map((s, i) => i === idx ? scheduleEditData : s);
                                                            setSchedule(updated);
                                                            localStorage.setItem(SCHEDULE_KEY, JSON.stringify(updated));
                                                            setEditingScheduleIdx(null);
                                                        }}
                                                    ><FaSave /></button>
                                                    <button
                                                        className={styles.btnDelete}
                                                        title="Cancelar"
                                                        onClick={() => setEditingScheduleIdx(null)}
                                                    ><FaTimes /></button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{idx + 1}</td>
                                            <td>{z.nombre}</td>
                                            <td>{z.manana}</td>
                                            <td>{z.tarde}</td>
                                            <td>
                                                <button
                                                    className={styles.btnEdit}
                                                    title="Editar"
                                                    onClick={() => { setScheduleEditData({ ...z }); setEditingScheduleIdx(idx); }}
                                                ><FaEdit /></button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ShippingZones;
